import { cn } from '@/lib/utils';
import type { MathfieldElement } from 'mathlive';
import 'mathlive/fonts.css';
import 'mathlive/static.css';
import { useEffect, useLayoutEffect, useRef } from 'react';
import './mathlive-init';

import type { BuilderPropertyRecord } from '../tool-settings';
import { applyMathfieldMacros, updateMathfieldKeyboardAndKeybindings } from './mathlive-keybindings';
import { buildEditorAtomicSymbolsForShortAnswer, resolveNotation, type EditorAtomicSymbol } from './mathlive-question-context';
import {
    getDeleteScenariosForShortAnswer,
    getInsertScenariosForShortAnswer,
    isMathliveDeleteInputType,
    isMathliveInsertInputType,
    runMatchingMathliveScenario,
    type MathliveScenarioEditor,
} from './mathlive-scenarios';

export type ShortAnswerMathliveAnswerFieldProps = {
    value: string;
    onChange: (latex: string) => void;
    readOnly?: boolean;
    className?: string;
    toolLabel: string;
    builderProps: BuilderPropertyRecord;
    placeholder?: string;
};

function getMathVirtualKeyboard() {
    return typeof window !== 'undefined' ? window.mathVirtualKeyboard : undefined;
}

function hideMathVirtualKeyboard() {
    const vk = getMathVirtualKeyboard();
    if (!vk) {
        return;
    }
    vk.visible = false;
    vk.hide({ animate: false });
}

function resetMathVirtualKeyboardToDefault() {
    const vk = getMathVirtualKeyboard();
    if (!vk) {
        return;
    }
    vk.layouts = 'default';
    hideMathVirtualKeyboard();
}

function isMathfieldDomReady(mathFieldElement: MathfieldElement): boolean {
    if (!mathFieldElement.isConnected) {
        return false;
    }
    try {
        void mathFieldElement.keybindings;
        return true;
    } catch {
        return false;
    }
}

function captureOriginalKeybindingsIfNeeded(
    mathFieldElement: MathfieldElement,
    originalKeybindingsRef: { current: MathfieldElement['keybindings'] | null },
): MathfieldElement['keybindings'] | null {
    if (originalKeybindingsRef.current != null) {
        return originalKeybindingsRef.current;
    }
    if (!isMathfieldDomReady(mathFieldElement)) {
        return null;
    }
    originalKeybindingsRef.current = [...mathFieldElement.keybindings];
    return originalKeybindingsRef.current;
}

function applyMathfieldScenarioSettings(
    mathFieldElement: MathfieldElement,
    builderProps: BuilderPropertyRecord,
    editorAtomicSymbols: EditorAtomicSymbol[],
) {
    const notation = resolveNotation(builderProps);

    mathFieldElement.scriptDepth = 1;
    mathFieldElement.smartSuperscript = false;
    mathFieldElement.smartFence = false;
    mathFieldElement.menuItems = [];

    if (notation !== 'numeric variables' && notation !== 'symbolic equivalence') {
        mathFieldElement.inlineShortcuts = {};
    } else {
        mathFieldElement.inlineShortcuts = {
            ...mathFieldElement.inlineShortcuts,
            dt: {},
            dx: {},
            dy: {},
            in: {},
            lb: {},
            or: {},
        } as unknown as NonNullable<MathfieldElement['inlineShortcuts']>;
    }

    applyMathfieldMacros(mathFieldElement, builderProps, editorAtomicSymbols);
}

export function ShortAnswerMathliveAnswerField({
    value,
    onChange,
    readOnly = false,
    className,
    builderProps,
    placeholder,
}: ShortAnswerMathliveAnswerFieldProps) {
    const mathFieldElementRef = useRef<MathfieldElement | null>(null);
    const previousUserAnswerRef = useRef(value);
    const previousCaretWasAtEndRef = useRef(true);
    const onChangeRef = useRef(onChange);
    const builderPropsRef = useRef(builderProps);
    const valueRef = useRef(value);
    const originalKeybindingsRef = useRef<MathfieldElement['keybindings'] | null>(null);
    const latexToMacroMapRef = useRef<Record<string, EditorAtomicSymbol>>({});
    const editorAtomicSymbolsRef = useRef<EditorAtomicSymbol[]>([]);

    // Use fefs hold the latest values without re-attaching listeners
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        valueRef.current = value;
        previousUserAnswerRef.current = value;
    }, [value]);

    useEffect(() => {
        builderPropsRef.current = builderProps;
        editorAtomicSymbolsRef.current = buildEditorAtomicSymbolsForShortAnswer(builderProps, latexToMacroMapRef.current);
    }, [builderProps]);

    useLayoutEffect(() => {
        let cancelled = false;
        let frameId = 0;

        const syncMathfield = () => {
            if (cancelled) {
                return;
            }

            const mathFieldElement = mathFieldElementRef.current;
            if (!mathFieldElement) {
                frameId = window.requestAnimationFrame(syncMathfield);
                return;
            }

            if (mathFieldElement.value !== value) {
                mathFieldElement.value = value;
            }

            if (!mathFieldElement.isConnected) {
                frameId = window.requestAnimationFrame(syncMathfield);
                return;
            }

            // Re-apply after mount; MathLive may not render a pre-connect assignment.
            mathFieldElement.value = value;

            mathFieldElement.readOnly = readOnly;

            if (placeholder != null && placeholder !== '') {
                mathFieldElement.setAttribute('placeholder', placeholder);
            } else {
                mathFieldElement.removeAttribute('placeholder');
            }

            const originalKeybindings = captureOriginalKeybindingsIfNeeded(mathFieldElement, originalKeybindingsRef);
            if (!originalKeybindings) {
                frameId = window.requestAnimationFrame(syncMathfield);
                return;
            }

            applyMathfieldScenarioSettings(mathFieldElement, builderProps, editorAtomicSymbolsRef.current);

            if (document.activeElement === mathFieldElement) {
                updateMathfieldKeyboardAndKeybindings(mathFieldElement, builderProps, {
                    expectedString: value,
                    editorAtomicSymbols: editorAtomicSymbolsRef.current,
                    originalKeybindings,
                });
            }
        };

        syncMathfield();

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frameId);
        };
    }, [value, readOnly, placeholder, builderProps]);

    useLayoutEffect(() => {
        const mathFieldElement = mathFieldElementRef.current;
        if (!mathFieldElement) {
            return;
        }

        const handleSelectionChange = () => {
            previousCaretWasAtEndRef.current = mathFieldElement.position === mathFieldElement.lastOffset;
        };

        const handleInput = (event: Event) => {
            const inputType = (event as InputEvent).inputType ?? '';
            let scenariosToTry = null;

            if (isMathliveDeleteInputType(inputType)) {
                scenariosToTry = getDeleteScenariosForShortAnswer(builderPropsRef.current);
            } else if (isMathliveInsertInputType(inputType)) {
                scenariosToTry = getInsertScenariosForShortAnswer(builderPropsRef.current);
            }

            if (scenariosToTry) {
                runMatchingMathliveScenario(
                    scenariosToTry,
                    mathFieldElement as MathliveScenarioEditor,
                    previousUserAnswerRef.current,
                    previousCaretWasAtEndRef.current,
                    valueRef.current,
                );
            }

            const userLatex = mathFieldElement.value;
            previousUserAnswerRef.current = userLatex;
            onChangeRef.current(userLatex);
        };

        const handleFocusIn = () => {
            window.setTimeout(() => {
                if (document.activeElement !== mathFieldElement && !mathFieldElement.contains(document.activeElement)) {
                    return;
                }
                const originalKeybindings = captureOriginalKeybindingsIfNeeded(mathFieldElement, originalKeybindingsRef);
                if (!originalKeybindings) {
                    return;
                }
                updateMathfieldKeyboardAndKeybindings(mathFieldElement, builderPropsRef.current, {
                    expectedString: valueRef.current,
                    editorAtomicSymbols: editorAtomicSymbolsRef.current,
                    originalKeybindings,
                });
                const vk = getMathVirtualKeyboard();
                if (vk) {
                    vk.visible = true;
                    vk.show({ animate: false });
                }
            }, 0);
        };

        const handleFocusOut = () => {
            window.setTimeout(() => {
                resetMathVirtualKeyboardToDefault();
            }, 0);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }
            const vk = getMathVirtualKeyboard();
            if (vk?.visible) {
                hideMathVirtualKeyboard();
            }
        };

        const virtualKeyboardToggle = mathFieldElement.shadowRoot?.querySelector('div[part=virtual-keyboard-toggle]');
        const handleVirtualKeyboardToggleClick = () => {
            const originalKeybindings = captureOriginalKeybindingsIfNeeded(mathFieldElement, originalKeybindingsRef);
            if (!originalKeybindings) {
                return;
            }
            updateMathfieldKeyboardAndKeybindings(mathFieldElement, builderPropsRef.current, {
                expectedString: valueRef.current,
                editorAtomicSymbols: editorAtomicSymbolsRef.current,
                originalKeybindings,
            });
            mathFieldElement.focus();
        };

        mathFieldElement.addEventListener('selection-change', handleSelectionChange);
        mathFieldElement.addEventListener('input', handleInput);
        mathFieldElement.addEventListener('keydown', handleKeyDown);
        mathFieldElement.addEventListener('focusin', handleFocusIn);
        mathFieldElement.addEventListener('focusout', handleFocusOut);
        virtualKeyboardToggle?.addEventListener('click', handleVirtualKeyboardToggleClick);

        return () => {
            mathFieldElement.removeEventListener('selection-change', handleSelectionChange);
            mathFieldElement.removeEventListener('input', handleInput);
            mathFieldElement.removeEventListener('keydown', handleKeyDown);
            mathFieldElement.removeEventListener('focusin', handleFocusIn);
            mathFieldElement.removeEventListener('focusout', handleFocusOut);
            virtualKeyboardToggle?.removeEventListener('click', handleVirtualKeyboardToggleClick);
            originalKeybindingsRef.current = null;
            resetMathVirtualKeyboardToDefault();
        };
    }, []);

    return (
        <math-field
            ref={mathFieldElementRef}
            math-virtual-keyboard-policy="sandboxed"
            className={cn(
                'border-input bg-background ring-offset-background focus-visible:ring-ring block text-lg w-full min-w-0 rounded-md border px-2 py-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                className,
            )}
        />
    );
}
